import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";

import { env } from "@/config/env";
import type {
	DriverPositionEvent,
	StopOutcomeEvent,
	TourStatusEvent,
} from "@/lib/logistics/types";
import { useAuthStore } from "@/stores/auth.store";

/**
 * Dérive l'origine WebSocket depuis l'URL API REST.
 * VITE_API_URL = http://host:port/api/v1 → http://host:port
 */
function wsOrigin(): string {
	try {
		const u = new URL(env.apiUrl);
		return `${u.protocol}//${u.host}`;
	} catch {
		return "";
	}
}

export interface LiveDriver extends DriverPositionEvent {
	/** Horodatage de réception côté client (pour la fraîcheur affichée). */
	receivedAt: number;
}

export interface UseLogisticsSocketResult {
	connected: boolean;
	/** Dernière position connue par livreur (clé = driverId). */
	positions: Record<string, LiveDriver>;
	/** Dernier statut de tournée reçu (clé = tourId). */
	tourStatuses: Record<string, TourStatusEvent>;
	/** Dernière issue d'arrêt reçue (clé = stopId). */
	stopOutcomes: Record<string, StopOutcomeEvent>;
}

/**
 * Client WebSocket temps réel logistique (LOG-06).
 * Se connecte à l'espace `/ws/logistics` avec le JWT admin, écoute les
 * positions GPS, statuts de tournée et issues d'arrêts — sans rechargement.
 */
export function useLogisticsSocket(enabled = true): UseLogisticsSocketResult {
	const token = useAuthStore((s) => s.accessToken);
	const socketRef = useRef<Socket | null>(null);
	const [connected, setConnected] = useState(false);
	const [positions, setPositions] = useState<Record<string, LiveDriver>>({});
	const [tourStatuses, setTourStatuses] = useState<
		Record<string, TourStatusEvent>
	>({});
	const [stopOutcomes, setStopOutcomes] = useState<
		Record<string, StopOutcomeEvent>
	>({});

	useEffect(() => {
		if (!enabled || !token) return;
		const origin = wsOrigin();
		if (!origin) return;

		const socket = io(`${origin}/ws/logistics`, {
			transports: ["websocket"],
			auth: { token },
			reconnection: true,
			reconnectionDelay: 1_000,
		});
		socketRef.current = socket;

		socket.on("connect", () => setConnected(true));
		socket.on("disconnect", () => setConnected(false));
		socket.on("connect_error", () => setConnected(false));

		socket.on("driver:position", (e: DriverPositionEvent) => {
			setPositions((prev) => ({
				...prev,
				[e.driverId]: { ...e, receivedAt: Date.now() },
			}));
		});
		socket.on("tour:status", (e: TourStatusEvent) => {
			setTourStatuses((prev) => ({ ...prev, [e.tourId]: e }));
		});
		socket.on("stop:outcome", (e: StopOutcomeEvent) => {
			setStopOutcomes((prev) => ({ ...prev, [e.stopId]: e }));
		});

		return () => {
			socket.removeAllListeners();
			socket.disconnect();
			socketRef.current = null;
		};
	}, [enabled, token]);

	return { connected, positions, tourStatuses, stopOutcomes };
}
